<template>
  <fieldset>
    <GooglePlacesTextField
      id="address_line_1"
      v-model="form.addressLine1"
      labelName="Address"
      name="address-line1"
      class="mb-16"
      :status="formStatuses.addressLine1"
      :helperText="helperText(formStatuses.addressLine1, 'Street address')"
      @autoCompletedAddresses="setAddresses"
      @blur="validateRequiredFields('addressLine1', form.addressLine1)"
    />
    <!-- Address Line 2 is always optional, so we do not validate it -->
    <PoppinsTextField
      id="address_line_2"
      v-model="form.addressLine2"
      labelName="Apartment, suite, etc (optional)"
      name="address-line2"
      type="text"
      class="mb-16"
    />
    <PoppinsTextField
      id="city"
      v-model="form.city"
      labelName="City"
      name="city"
      type="text"
      class="mb-16"
      :status="formStatuses.city"
      :helperText="helperText(formStatuses.city, 'City')"
      @blur="validateRequiredFields('city', form.city)"
    />
    <PoppinsDropdown
      id="state"
      v-model="form.stateCode"
      labelName="State"
      name="state"
      class="mb-16"
      :options="states"
      :status="formStatuses.stateCode"
      :helperText="helperText(formStatuses.stateCode, 'State')"
      @blur="validateRequiredFields('stateCode', form.stateCode)"
    />
    <PoppinsTextField
      id="postal_code"
      v-model="form.postalCode"
      labelName="ZIP code"
      name="postal-code"
      type="text"
      maxlength="5"
      pattern="[0-9]*"
      class="mb-24"
      inputmode="numeric"
      :status="formStatuses.postalCode"
      :helperText="helperText(formStatuses.postalCode, 'ZIP code', form.postalCode)"
      @blur="validateZipcode"
    />
  </fieldset>
</template>

<script lang="ts">
import TextField from '../textfield/TextField.vue';
import Dropdown from '../dropdown/Dropdown.vue';
import helperStatus from '../../helpers/status';
import states from '../../helpers/states';
import GooglePlacesTextField from './GooglePlacesTextField.vue';
import { normalizeGooglePlacesAddress } from '../../helpers/addressHelper';

function initialState() {
  return {
    placeId: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateCode: '',
    postalCode: '',
  };
}

export default {
  name: 'AddressAutoComplete',
  components: {
    PoppinsTextField: TextField,
    PoppinsDropdown: Dropdown,
    GooglePlacesTextField,
  },
  props: {
    requiredFields: {
      type: Array,
      default: () => ['addressLine1', 'city', 'stateCode', 'postalCode'],
    },
    storedAddress: {
      type: Object,
      default: () => {},
    },
  },
  data(): any {
    return {
      states,
      form: (this as any).initializeForm(),
      formStatuses: {
        addressLine1: helperStatus.Neutral,
        postalCode: helperStatus.Neutral,
        city: helperStatus.Neutral,
        stateCode: helperStatus.Neutral,
      },
    };
  },
  computed: {
    isValid() {
      return Object.keys(this.formStatuses).every((key) => {
        return this.formStatuses[key] === helperStatus.Neutral;
      });
    },
  },
  watch: {
    'form.addressLine1': function (newValue) {
      this.onInput();
      if (!this.fieldIsEmpty(newValue)) {
        this.formStatuses.addressLine1 = helperStatus.Neutral;
      }
    },
    'form.addressLine2': function (newValue) {
      if (newValue) {
        this.onInput();
      }
    },
    'form.city': function (newValue) {
      this.onInput();
      if (!this.fieldIsEmpty(newValue)) {
        this.formStatuses.city = helperStatus.Neutral;
      }
    },
    'form.stateCode': function (newValue) {
      this.onInput();
      if (newValue) {
        this.formStatuses.stateCode = helperStatus.Neutral;
      }
    },
    'form.postalCode': function (newValue) {
      this.onInput();
      if (this.zipcodeIsValid(newValue)) {
        this.formStatuses.postalCode = helperStatus.Neutral;
      }
    },
  },
  created() {
    this.initializeForm();
  },
  methods: {
    onInput() {
      this.$emit('input', this.form);
    },
    initializeForm() {
      if (this.storedAddress) {
        this.form = this.storedAddress;
      } else {
        this.form = initialState();
      }
    },
    setAddresses(placeData) {
      const normalizedAddress = normalizeGooglePlacesAddress(placeData);
      if (!normalizedAddress) {
        return false;
      }

      this.form = {
        ...normalizedAddress.components,
        addressLine2: this.form.addressLine2 || '',
      };
      this.onInput();
      this.validateAll();

      document.getElementById('addressLine2')?.focus();
      return false;
    },
    validateRequiredFields(fieldName, fieldValue) {
      if (this.fieldIsRequired(fieldName) && this.fieldIsEmpty(fieldValue)) {
        this.formStatuses[fieldName] = helperStatus.Error;
      }
    },
    validateZipcode() {
      this.validateRequiredFields('postalCode', this.form.postalCode);
      if (!this.zipcodeIsValid(this.form.postalCode)) {
        this.formStatuses.postalCode = helperStatus.Error;
      }
    },
    validateAll() {
      Object.entries(this.form).forEach((e) => {
        this.validateRequiredFields(e[0], e[1]);
      });
      this.validateZipcode();

      return this.isValid;
    },
    fieldIsEmpty(fieldValue) {
      return fieldValue.length === 0;
    },
    fieldIsRequired(fieldName) {
      return this.requiredFields.includes(fieldName);
    },
    zipcodeIsValid(value) {
      const zipCodeRegex = /^\d{5}$/;
      return zipCodeRegex.test(value);
    },
    helperText(status: helperStatus, type: string, value: string) {
      // Error helper text
      if (status === helperStatus.Error) {
        if (type === 'ZIP code' && value.length > 0) {
          return 'Enter a five digit ZIP code';
        }
        return `${type} is required`;
      }
      return '';
    },
  },
};
</script>
